import { type ComponentType, useState, useSyncExternalStore } from 'react'

const subscribeNever = () => () => undefined

/**
 * A `next/dynamic` page body that can be downloaded ahead of the click. Once
 * `preload()` has loaded the module, a NEW mount renders the component
 * directly — no lazy wrapper, so no `loading` bones frame on a client
 * navigation. Otherwise (and always while hydrating, so the client tree
 * matches the wrapper the server emitted) it renders `Lazy`.
 *
 * The choice is latched once per mount and never switched afterwards: a
 * different component type would remount the body and drop its state.
 * `Lazy` must be the module's own inline `dynamic(() => import(…))` call,
 * which Next compiles to emit the chunk's preload hints.
 */
export function createPreloadableComponent<Props extends object>(
  load: () => Promise<ComponentType<Props>>,
  Lazy: ComponentType<Props>
) {
  let loaded: ComponentType<Props> | null = null

  /** Download the module. Safe to call repeatedly. */
  function preload(): Promise<void> {
    if (loaded) {
      return Promise.resolve()
    }
    return load().then((component) => {
      loaded = component
    })
  }

  /**
   * The component to render, latched the first time `isNeeded` is true;
   * `null` before that.
   */
  function useLatchedComponent(isNeeded = true): ComponentType<Props> | null {
    const isHydrating = useSyncExternalStore(
      subscribeNever,
      () => false,
      () => true
    )
    const [Component, setComponent] = useState<ComponentType<Props> | null>(
      null
    )
    if (isNeeded && Component === null) {
      setComponent(() => (isHydrating ? null : loaded) ?? Lazy)
    }
    return Component
  }

  return { preload, useLatchedComponent }
}
